import NoIconImage from '../../assets/placeholder-image-small.png?inline';
import ViewOnScreen from '../../components/view-on-screen/ViewOnScreen';
import { CartItem, CartOperationType } from '../../types/cart';
import { dispatch, RootState, useAppSelector } from '../../redux/store';
import { getDateString } from '../../utils/textUtilities';
import { openCartModal, updateCartState } from '../../redux/slice/cartSlice';
import { redirectToCart, redirectToCategories, redirectToEquipmentClass } from '../../utils/router';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  CartItemsContainer,
  CartHeaderItem,
  CartItemImageWrapper,
  CartItemImage,
  CartItemDetailsContainer,
  CartItemName,
  CartItemSelectedDates,
  CartItemUpdateContainer,
  StyledPencilIcon,
  StyledDeleteIcon,
  EstimatedTotalContainer,
  EstimatedTotalLabel,
  EstimatedTotal,
  CheckoutButton,
  ContinueShoppingButton,
  BrowseEquipmentButton,
  BrowseSelectionLabel,
} from './CartHeaderStyle';

export default function CartHeaderContent() {
  const cartItemsContainerRef = useRef<HTMLDivElement>(null);
  const elementsRefs: React.RefObject<HTMLDivElement>[] = [];
  const config = useAppSelector((state: RootState) => state.cart.cartHeaderConfig);
  const cart = useAppSelector((state: RootState) => state.cart);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean | undefined>(false);
  const [index, setIndex] = useState<number | undefined>();
  const [maxItemsToShow, setMaxItemsToShow] = useState<number | undefined>();

  const theme = useTheme();
  const isSmallScreenBreakpoint = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.xs),
  );

  useEffect(() => {
    setOpen(config?.open);
    setIndex(config?.index);
    setMaxItemsToShow(config?.maxItemsToShow);
  }, [config]);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(cart.isLoadingFlags.quote);
    }, 0);
  }, [cart.isLoadingFlags.quote]);

  useEffect(() => {
    if (open && cartItemsContainerRef.current) {
      const cartItemsContainer = cartItemsContainerRef.current;
      if (cart.cartItems.length == 0) {
        cartItemsContainer.scrollTop = cartItemsContainer.scrollHeight;
      } else {
        let item: HTMLElement;
        if (maxItemsToShow && cart.cartItems.length > maxItemsToShow) {
          item = cartItemsContainer.querySelector(
            `#cart-item-${String(maxItemsToShow)}`,
          ) as HTMLElement;
          const itemOffset = item.offsetTop;
          const itemHeight = item.offsetHeight;
          const itemMiddle = itemOffset - itemHeight / 1.3;
          cartItemsContainer.style.maxHeight = `${itemMiddle}px`;
        }
        if (index && index !== -1) {
          item = cartItemsContainer.querySelector(`#cart-item-${String(index)}`) as HTMLElement;
        } else {
          item = cartItemsContainer.querySelector(
            `#cart-item-${String(cart.cartItems.length - 1)}`,
          ) as HTMLElement;
        }
        const itemOffset = item.offsetTop;
        const cartItemsContainerScrollTop = cartItemsContainer.scrollTop;
        const cartItemsContainerOffset = cartItemsContainer.getBoundingClientRect().top;
        const offset = itemOffset - cartItemsContainerOffset + cartItemsContainerScrollTop;
        if (!isSmallScreenBreakpoint) {
          cartItemsContainer.scrollTo({
            top: offset,
            //behavior: 'smooth',
          });
        }
      }
    }
  }, [open]);

  const handleCartLinkClick = (slug: string) => {
    redirectToEquipmentClass(slug);
  };

  const handleEdit = (cartItem: CartItem, index: number) => {
    dispatch(
      openCartModal({
        index,
        cartItem,
        cartOperationType: CartOperationType.UPDATE,
        toggleCartHeaderOnUpdate: true,
      }),
    );
  };

  const handleDelete = (index: number) => {
    const payload = {
      index: index,
      actionType: CartOperationType.REMOVE,
    };
    dispatch(updateCartState(payload));
  };

  return cart.cartItems.length > 0 ? (
    <>
      <CartItemsContainer ref={cartItemsContainerRef}>
        {cart.cartItems.map((item: CartItem, index: number) => {
          return (
            <CartHeaderItem
              id={`cart-item-${String(index)}`}
              key={String(index) + ':' + String(item.equipmentId)}
              ref={(ref) => (elementsRefs[index] = ref as React.RefObject<HTMLDivElement>)}
            >
              <CartItemImageWrapper>
                <CartItemImage
                  src={item.imageSource ?? ''}
                  alt={`image for ${item.name}`}
                  onError={(e) => {
                    const imageElement = e.target as HTMLImageElement;
                    imageElement.src = NoIconImage;
                  }}
                />
              </CartItemImageWrapper>
              <CartItemDetailsContainer>
                <CartItemName onClick={() => handleCartLinkClick(item.slug as string)}>
                  {item.quantity}
                  {' x '}
                  {item.name ?? ''}
                </CartItemName>
                <CartItemSelectedDates>
                  {getDateString(item.startDate, item.endDate)}
                </CartItemSelectedDates>
              </CartItemDetailsContainer>
              <CartItemUpdateContainer>
                <StyledPencilIcon onClick={() => handleEdit(item, index)}></StyledPencilIcon>
                <StyledDeleteIcon onClick={() => handleDelete(index)} />
              </CartItemUpdateContainer>
            </CartHeaderItem>
          );
        })}
      </CartItemsContainer>
      <EstimatedTotalContainer>
        <EstimatedTotalLabel>Estimated Total:</EstimatedTotalLabel>
        <EstimatedTotal amount={cart.quote?.rental_subtotal} isLoading={isLoading} />
      </EstimatedTotalContainer>
      <CheckoutButton onClick={redirectToCart}>VIEW CART & checkout</CheckoutButton>
      <ContinueShoppingButton onClick={redirectToCategories}>
        continue shopping
      </ContinueShoppingButton>
    </>
  ) : (
    <>
      <ViewOnScreen screen={{ down: 'xs' }}>
        <BrowseSelectionLabel>Get started by browsing our selection</BrowseSelectionLabel>
      </ViewOnScreen>
      <BrowseEquipmentButton onClick={redirectToCategories}>browse equipment</BrowseEquipmentButton>
    </>
  );
}
